import React from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';

export interface EmptyStateProps {
  icon?: LucideIcon;
  title?: string;
  message?: string;
  description?: string;
  action?: string;
  actionLabel?: string;
  actions?: React.ReactNode;
  onAction?: () => void;
  className?: string;
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  message,
  description,
  action,
  actionLabel,
  actions,
  onAction,
  className,
}: EmptyStateProps) {
  const displayMessage = message || description;
  const displayAction = action || actionLabel;

  return (
    <div
      className={cn(
        'experience-candidate-empty-state relative flex flex-col items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white px-6 py-10 text-center shadow-sm',
        className,
      )}
    >
      <div className="experience-candidate-empty-rule absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-statura-green-accessible/30 to-transparent" />
      <div className="experience-candidate-empty-icon flex h-12 w-12 items-center justify-center rounded-lg border border-statura-green-accessible/15 bg-statura-green-accessible/10">
        <Icon className="h-6 w-6 text-statura-green-accessible" aria-hidden="true" />
      </div>
      {title && (
        <p className="experience-candidate-heading mt-4 max-w-xl font-heading text-card-title text-text-heading">
          {title}
        </p>
      )}
      {displayMessage && (
        <p
          className={cn(
            'experience-candidate-body max-w-2xl font-body text-body-sm text-text-body',
            title ? 'mt-2' : 'mt-4',
          )}
        >
          {displayMessage}
        </p>
      )}
      {actions && <div className="mt-5 flex flex-wrap justify-center gap-2">{actions}</div>}
      {!actions && displayAction && onAction && (
        <button
          onClick={onAction}
          className="experience-candidate-button experience-candidate-button-primary experience-candidate-motion experience-candidate-focus mt-5 inline-flex min-h-10 items-center justify-center rounded-lg border border-statura-green-accessible bg-statura-green-accessible px-4 py-2 font-body text-body-sm font-semibold text-white shadow-sm transition-interactive duration-200 hover:bg-statura-green-accessible-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible focus-visible:ring-offset-2"
        >
          {displayAction}
        </button>
      )}
    </div>
  );
}
